import { useState } from "react";
import { X, Loader2, AlertTriangle } from "lucide-react";
import useMarkGunFaulty from "../../hooks/useMarkGunFaulty";

export default function MarkGunFaultyDialog({ onClose }) {
  const {
    markGunFaulty,
    isLoading,
    error: hookError,
  } = useMarkGunFaulty();

  const [stationId, setStationId] = useState("");
  const [gunId, setGunId] = useState("");
  const [isFaulty, setIsFaulty] = useState(true); // Default to true
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    setError("");

    if (!stationId.trim()) {
      setError("Station ID is required");
      return;
    }

    if (!gunId.trim()) {
      setError("Gun ID is required");
      return;
    }

    const res = await markGunFaulty({
      station_id: stationId,
      gun_id: gunId,
      is_faulty: isFaulty,
      message: message.trim() || "No message provided",
    });

    if (res.success) {
      onClose();
    } else {
      setError(res.error || "Failed to mark gun status");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white w-full max-w-md rounded-xl shadow-xl border border-gray-200">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div className="flex items-center gap-2">
            <AlertTriangle size={18} className="text-amber-600" />
            <h2 className="text-lg font-semibold text-gray-900">
              Mark Gun Status
            </h2>
          </div>

          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition"
          >
            <X size={18} className="text-gray-700" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-5 space-y-4">

          {/* Status Toggle */}
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
            <div>
              <p className="text-sm font-medium text-gray-900">Faulty Status</p>
              <p className="text-xs text-gray-500">Is this gun currently broken?</p>
            </div>
            <button
              onClick={() => setIsFaulty(!isFaulty)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                isFaulty ? "bg-red-500" : "bg-gray-300"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  isFaulty ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
          </div>

          {/* Station ID */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Station ID
            </label>
            <input
              value={stationId}
              onChange={(e) => setStationId(e.target.value)}
              placeholder="e.g. 8"
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10"
            />
          </div>

          {/* Gun ID */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Gun ID
            </label>
            <input
              value={gunId}
              onChange={(e) => setGunId(e.target.value)}
              placeholder="e.g. yang"
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10"
            />
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Fault Message / Reason
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="e.g. Cable damaged"
              rows={2}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 resize-none"
            />
          </div>

          {(error || hookError) && (
            <p className="text-sm text-red-600 bg-red-50 p-2 rounded border border-red-100">
              {error || hookError}
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
          >
            Cancel
          </button>

          <button
            disabled={isLoading}
            onClick={handleSubmit}
            className={`px-4 py-2 rounded-lg text-white flex items-center gap-2 transition disabled:opacity-50 ${
              isFaulty ? "bg-red-600 hover:bg-red-700" : "bg-black hover:bg-gray-900"
            }`}
          >
            {isLoading && <Loader2 size={16} className="animate-spin" />}
            Update Status
          </button>
        </div>

      </div>
    </div>
  );
}